<script setup lang="ts">
/**
 * Inline interactive building block for the `render_number_input` tool: a numeric
 * answer collected in-chat. Emits the entered number; the dispatcher wraps it
 * into the tool's result shape (`value`).
 */
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  NumberField,
  NumberFieldContent,
  NumberFieldDecrement,
  NumberFieldIncrement,
  NumberFieldInput,
} from "@/components/ui/number-field";
import { Button } from "@/components/ui/button";

const props = withDefaults(
  defineProps<{
    input: Record<string, unknown>;
    state: string;
    disabled?: boolean;
  }>(),
  { disabled: false },
);

const emit = defineEmits<{ (e: "submit", value: number): void }>();

const { t } = useI18n({ useScope: "local" });

const title = computed(() => (typeof props.input.title === "string" ? props.input.title : ""));
const placeholder = computed(() =>
  typeof props.input.placeholder === "string" ? props.input.placeholder : t("placeholder"),
);

const resolved = computed(
  () =>
    props.disabled ||
    props.state === "output-available" ||
    props.state === "approval-responded" ||
    props.state === "output-error" ||
    props.state === "output-denied",
);

const value = ref<number | undefined>(undefined);
const submitted = ref(false);
const readOnly = computed(() => resolved.value || submitted.value);
const hasValue = computed(() => typeof value.value === "number" && !Number.isNaN(value.value));

function submit() {
  if (readOnly.value || !hasValue.value) {
    return;
  }
  submitted.value = true;
  emit("submit", value.value as number);
}
</script>

<template>
  <div class="not-prose space-y-3 rounded-xl border border-border bg-card p-4" data-slot="chat-block-number">
    <p v-if="title" class="text-sm font-medium text-foreground">{{ title }}</p>

    <NumberField v-model="value" :disabled="readOnly">
      <NumberFieldContent>
        <NumberFieldDecrement />
        <NumberFieldInput :placeholder="placeholder" />
        <NumberFieldIncrement />
      </NumberFieldContent>
    </NumberField>

    <div v-if="!resolved" class="flex justify-end">
      <Button type="button" size="sm" :disabled="!hasValue || submitted" @click="submit">
        {{ t("submit") }}
      </Button>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  submit: Submit
  placeholder: Enter a number…
</i18n>
